export type Rect = { x: number; y: number; w: number; h: number }
export type Tile<T> = Rect & { node: T }

const sum = (values: readonly number[]) => values.reduce((total, value) => total + value, 0)

/**
 * Squarified treemap (Bruls, Huizing & van Wijk): lays nodes out in rows along the shorter side, adding a
 * node to the current row only while that improves the row's worst aspect ratio, so tiles stay close to
 * square and small parts of the surface remain visible and clickable.
 */
export const squarify = <T>(nodes: readonly { node: T; value: number }[], rect: Rect): Tile<T>[] => {
  const total = sum(nodes.map(each => each.value))
  if (!total || rect.w <= 0 || rect.h <= 0) {
    return []
  }
  const scale = (rect.w * rect.h) / total
  const items = nodes.map(each => ({ node: each.node, area: each.value * scale })).sort((a, b) => b.area - a.area)
  const tiles: Tile<T>[] = []
  let { x, y, w, h } = rect

  const worst = (row: readonly { area: number }[], side: number) => {
    const rowArea = sum(row.map(item => item.area))
    const largest = Math.max(...row.map(item => item.area))
    const smallest = Math.min(...row.map(item => item.area))
    return Math.max((side * side * largest) / (rowArea * rowArea), (rowArea * rowArea) / (side * side * smallest))
  }
  const layRow = (row: readonly { node: T; area: number }[]) => {
    const rowArea = sum(row.map(item => item.area))
    if (w >= h) {
      const columnWidth = rowArea / h
      let top = y
      for (const item of row) {
        const height = item.area / columnWidth
        tiles.push({ node: item.node, x, y: top, w: columnWidth, h: height })
        top += height
      }
      x += columnWidth
      w -= columnWidth
    } else {
      const rowHeight = rowArea / w
      let left = x
      for (const item of row) {
        const width = item.area / rowHeight
        tiles.push({ node: item.node, x: left, y, w: width, h: rowHeight })
        left += width
      }
      y += rowHeight
      h -= rowHeight
    }
  }

  let row: { node: T; area: number }[] = []
  for (const item of items) {
    const side = Math.min(w, h)
    if (row.length === 0 || worst([...row, item], side) <= worst(row, side)) {
      row.push(item)
    } else {
      layRow(row)
      row = [item]
    }
  }
  if (row.length > 0) {
    layRow(row)
  }
  return tiles
}
